<template>
  <div>
    <!-- Sidebar: New Request -->
    <b-sidebar
      id="new-request-sidebar"
      v-model="visibility5"
      bg-variant="white"
      backdrop
      shadow
      no-header
      right
      class="resource-sidebar"
    >
      <div class="sb-header sb-header--approve">
        <div class="sb-header__top">
          <div class="sb-header__badge">
            <feather-icon icon="CheckCircleIcon" size="14" />
            <span>New student request</span>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            class="sb-close-btn"
            @click="visibility5 = false"
          >
            <feather-icon icon="XIcon" size="16" />
          </b-button>
        </div>
        <div class="sb-header__profile">
          <b-avatar
            size="52"
            variant="light-success"
            :text="sidebarInitials"
            class="sb-avatar"
          />
          <div class="sb-header__info">
            <h5 class="sb-header__name" v-html="sidebarTitle"></h5>
          </div>
        </div>
      </div>

      <div class="sb-body">
        <!-- Step: choose search vs new admission -->
        <div v-if="newRequestStep === 'menu'" class="sb-form-stack">
          <p class="text-muted small mb-2">
            Search by Federal Board registration number, or create a new
            admission request without linking a student yet.
          </p>
          <b-button
            variant="primary"
            block
            class="sb-action-btn sb-action-btn--view mb-1"
            @click="beginSearchStudentFlow"
          >
            <feather-icon icon="SearchIcon" size="16" class="mr-50" />
            <span>Search student (Federal Board No.)</span>
          </b-button>
          <b-button
            variant="outline-primary"
            block
            class="sb-action-btn sb-action-btn--view"
            @click="beginNewAdmissionFlow"
          >
            <feather-icon icon="UserPlusIcon" size="16" class="mr-50" />
            <span>New admission request</span>
          </b-button>
        </div>

        <!-- Step: federal board search + results -->
        <div v-else-if="newRequestStep === 'search'">
          <b-button
            variant="link"
            class="p-0 mb-2 text-muted"
            size="sm"
            @click="backToNewRequestMenu"
          >
            <feather-icon icon="ArrowLeftIcon" size="14" class="mr-50" />
            Back
          </b-button>
          <b-input-group class="mb-2">
            <b-form-input
              v-model.trim="federalBoardQuery"
              placeholder="Federal Board registration no."
              @keyup.enter="searchStudentsByFederalBoard"
            />
            <b-input-group-append>
              <b-button
                variant="primary"
                :disabled="studentSearchLoading"
                @click="searchStudentsByFederalBoard"
              >
                <b-spinner v-if="studentSearchLoading" small />
                <span v-else>Search</span>
              </b-button>
            </b-input-group-append>
          </b-input-group>

          <div
            v-if="
              !studentSearchLoading &&
              studentSearchResults.length === 0 &&
              federalBoardSearchAttempted
            "
            class="text-muted small mb-2"
          >
            No students found. Try another number.
          </div>

          <div class="sb-student-pick-list">
            <button
              v-for="row in studentSearchResults"
              :key="'pick-' + row.id"
              type="button"
              class="sb-student-pick-item"
              @click="selectStudentForNewRequest(row)"
            >
              <div
                class="d-flex justify-content-between align-items-start w-100"
              >
                <div class="font-weight-bold text-left mr-50">
                  {{ row.name }}
                </div>
                <b-badge
                  v-if="row.status"
                  pill
                  class="ml-50 flex-shrink-0"
                  :variant="statusVariant(row.status)"
                >
                  {{ row.status }}
                </b-badge>
              </div>
              <small class="text-muted text-left d-block">
                {{
                  reportDomain === "myskoolhpgs" ||
                  reportDomain === "myskoolprofectus"
                    ? "GR: " + (row.grNoLocal || row.grNo || "—")
                    : "GR: " + (row.grNo || "—")
                }}
                <span v-if="row.meta_class"> · {{ row.meta_class }}</span>
              </small>
              <small
                v-if="row.federalRegNo"
                class="text-muted text-left d-block"
              >
                Federal #: {{ row.federalRegNo }}
              </small>
            </button>
          </div>
        </div>

        <!-- Step: form (after student picked or new admission) -->
        <b-form v-else-if="newRequestStep === 'form'" @submit.prevent>
          <b-button
            v-if="!newAdmissionMode"
            variant="link"
            class="p-0 mb-2 text-muted"
            size="sm"
            @click="backFromNewRequestForm"
          >
            <feather-icon icon="ArrowLeftIcon" size="14" class="mr-50" />
            Change student
          </b-button>

          <div
            v-if="selectedStudentForRequest && !newAdmissionMode"
            class="sb-selected-student mb-2"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="font-weight-bold">
                  {{ selectedStudentForRequest.name }}
                </div>
                <small class="text-muted d-block">
                  {{
                    reportDomain === "myskoolhpgs" ||
                    reportDomain === "myskoolprofectus"
                      ? "GR: " +
                        (selectedStudentForRequest.grNoLocal ||
                          selectedStudentForRequest.grNo ||
                          "—")
                      : "GR: " + (selectedStudentForRequest.grNo || "—")
                  }}
                  <span v-if="selectedStudentForRequest.meta_class">
                    · {{ selectedStudentForRequest.meta_class }}</span
                  >
                </small>
                <small
                  v-if="selectedStudentForRequest.federalRegNo"
                  class="text-muted d-block"
                >
                  Federal #: {{ selectedStudentForRequest.federalRegNo }}
                </small>
                <b-badge
                  v-if="selectedStudentForRequest.status"
                  pill
                  class="mt-50"
                  :variant="statusVariant(selectedStudentForRequest.status)"
                >
                  {{ selectedStudentForRequest.status }}
                </b-badge>
              </div>
              <b-button
                variant="outline-danger"
                size="sm"
                class="flex-shrink-0"
                @click="clearSelectedStudentForNewRequest"
              >
                <feather-icon icon="XIcon" size="14" />
              </b-button>
            </div>
          </div>

          <div class="sb-form-field">
            <b-form-group
              label-class="p-0"
              :invalid-feedback="$t('Date is required')"
              ref="req_date"
            >
              <label class="sb-label">
                <feather-icon icon="CalendarIcon" size="13" class="mr-50" />
                {{ $t("Request on") }}
              </label>
              <flat-pickr
                :config="config"
                v-model="reqObj.request_date"
                class="form-control sb-input"
                :placeholder="$t('Select Date')"
              />
            </b-form-group>
          </div>

          <div v-if="newAdmissionMode" class="sb-form-field">
            <b-form-group
              label-class="p-0"
              invalid-feedback="Student name, father name, Federal Board registration no., and a valid WhatsApp number are required."
              ref="req_new_admission_details"
            >
              <label class="sb-label">
                <feather-icon icon="UserPlusIcon" size="13" class="mr-50" />
                New admission details
              </label>
              <div class="sb-new-admission-grid">
                <div class="sb-na-field">
                  <label class="sb-na-label"
                    >Federal Board Registration No</label
                  >
                  <b-form-input
                    v-model.trim="newAdmissionForm.federal_board_reg_no"
                    class="sb-input"
                    placeholder="Federal Board Registration No"
                  />
                </div>
                <div class="sb-na-field">
                  <label class="sb-na-label">Matric Roll No</label>
                  <b-form-input
                    v-model.trim="newAdmissionForm.matric_roll_no"
                    class="sb-input"
                    placeholder="Matric Roll No"
                  />
                </div>
                <div class="sb-na-field">
                  <label class="sb-na-label">Student Name</label>
                  <b-form-input
                    v-model.trim="newAdmissionForm.student_name"
                    class="sb-input"
                    placeholder="Student Name"
                  />
                </div>
                <div class="sb-na-field">
                  <label class="sb-na-label">Father Name</label>
                  <b-form-input
                    v-model.trim="newAdmissionForm.father_name"
                    class="sb-input"
                    placeholder="Father Name"
                  />
                </div>
                <div class="sb-na-field">
                  <label class="sb-na-label">WhatsApp No</label>
                  <vue-tel-input
                    v-model="newAdmissionForm.whatsapp_no"
                    :inputOptions="newAdmissionTelInputOptions.whatsapp"
                    :dropdownOptions="newAdmissionTelDropdownOptions"
                    :defaultCountry="$store.state.countryCode"
                    @input="checkNewAdmissionWhatsapp"
                  />
                  <small
                    v-if="newAdmissionWhatsappPhoneError"
                    class="text-danger d-block mt-25"
                    >{{ $t("Please enter correct contact number") }}</small
                  >
                </div>
                <div class="sb-na-field">
                  <label class="sb-na-label">Student Contact No</label>
                  <vue-tel-input
                    v-model="newAdmissionForm.student_contact"
                    :inputOptions="newAdmissionTelInputOptions.student"
                    :dropdownOptions="newAdmissionTelDropdownOptions"
                    :defaultCountry="$store.state.countryCode"
                  />
                </div>
                <div class="sb-na-field">
                  <label class="sb-na-label">Parent's Contact</label>
                  <vue-tel-input
                    v-model="newAdmissionForm.parent_contact"
                    :inputOptions="newAdmissionTelInputOptions.parent"
                    :dropdownOptions="newAdmissionTelDropdownOptions"
                    :defaultCountry="$store.state.countryCode"
                  />
                </div>
                <div class="sb-na-field">
                  <label class="sb-na-label">Note</label>
                  <b-form-textarea
                    v-model.trim="newAdmissionForm.note"
                    class="sb-textarea"
                    rows="3"
                    placeholder="Note"
                  />
                </div>
              </div>
            </b-form-group>
          </div>

          <div v-else class="sb-form-field">
            <b-form-group
              label-class="p-0"
              :invalid-feedback="$t('Reason is required.')"
              ref="req_reason"
            >
              <label class="sb-label">
                <feather-icon icon="FileTextIcon" size="13" class="mr-50" />
                {{ $t("Reason") }}
              </label>
              <b-form-textarea
                :placeholder="$t('Enter reason')"
                rows="3"
                v-model.trim="reqObj.reason"
                class="sb-textarea"
              />
            </b-form-group>
          </div>

          <div class="sb-form-field">
            <b-form-group
              label-class="p-0"
              :invalid-feedback="$t('Type is required')"
              ref="req_st"
            >
              <label class="sb-label">
                <feather-icon icon="LayersIcon" size="13" class="mr-50" />
                {{ $t("Request Type") }}
              </label>
              <v-select
                v-model="reqObj.type"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="leaveReqTypeOptions"
                :reduce="(opt) => opt.value"
                label="text"
                :clearable="false"
                :disabled="newAdmissionMode"
                :placeholder="$t('Select type')"
                class="sb-vselect"
              />
            </b-form-group>
          </div>

          <div class="sb-form-field">
            <label class="sb-label">
              <feather-icon icon="UploadCloudIcon" size="13" class="mr-50" />
              {{ $t("Documents") }}
            </label>
            <div class="d-flex flex-wrap">
              <div
                class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1 bg-white"
                style="width: 110px; height: 96px"
                @click="!newReqDocLoading && $refs.newLeaveReqDocsInput.click()"
              >
                <b-spinner
                  v-if="newReqDocLoading"
                  small
                  :label="$t('Loading') + '...'"
                  variant="primary"
                />
                <feather-icon
                  v-else
                  icon="PlusIcon"
                  size="34"
                  class="text-primary"
                />
              </div>
              <div
                class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1 bg-white"
                style="width: 110px; height: 96px; position: relative"
                v-for="(file, index) in newReqDocList"
                :key="'new-req-doc-' + index"
                @click="openFile(file)"
              >
                <b-avatar
                  size="20"
                  variant="primary"
                  style="position: absolute; top: -5px; right: -5px"
                  button
                  @click.stop="removeNewRequestDoc(index)"
                >
                  <feather-icon icon="XIcon" size="10" />
                </b-avatar>
                <b-img
                  v-if="isImage(file)"
                  :src="getDocPreviewUrl(file)"
                  class="doc-thumb-img"
                  alt="preview"
                  referrerpolicy="no-referrer"
                  @error="onDocPreviewError($event, file)"
                />
                <feather-icon
                  v-else
                  icon="FileTextIcon"
                  size="30"
                  class="text-primary"
                />
              </div>

              <input
                type="file"
                id="newLeaveReqDocsInput"
                hidden
                ref="newLeaveReqDocsInput"
                multiple
                @change="addNewRequestDoc()"
                accept="image/*,.pdf"
              />
            </div>
          </div>

          <b-button
            variant="success"
            :disabled="savingNewRequest"
            block
            class="sb-action-btn sb-action-btn--approve mt-2"
            @click="saveNewLeaveRequest()"
          >
            <b-spinner
              v-if="savingNewRequest"
              small
              type="grow"
              class="mr-50"
            />
            <feather-icon v-else icon="CheckIcon" size="16" class="mr-50" />
            <span>{{ savingNewRequest ? "Processing..." : $t("Save") }}</span>
          </b-button>
        </b-form>
      </div>
    </b-sidebar>

    <!-- Sidebar: Approve -->
    <b-sidebar
      id="approve-sidebar"
      v-model="visibility"
      bg-variant="white"
      backdrop
      shadow
      no-header
      right
      class="resource-sidebar"
    >
      <div class="sb-header sb-header--approve">
        <div class="sb-header__top">
          <div class="sb-header__badge">
            <feather-icon icon="CheckCircleIcon" size="14" />
            <span>Approve Request</span>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            class="sb-close-btn"
            @click="visibility = false"
          >
            <feather-icon icon="XIcon" size="16" />
          </b-button>
        </div>
        <div class="sb-header__profile">
          <b-avatar
            size="52"
            variant="light-success"
            :text="sidebarInitials"
            class="sb-avatar"
          />
          <div class="sb-header__info">
            <h5 class="sb-header__name" v-html="sidebarTitle"></h5>
          </div>
        </div>
      </div>

      <div class="sb-body">
        <RequestDetails :reqItem="approveObj" />

        <div class="sb-divider">
          <span>Action</span>
        </div>

        <b-form @submit.prevent>
          <div class="sb-form-field">
            <b-form-group
              label-class="p-0"
              invalid-feedback="Date is required"
              ref="date"
            >
              <label class="sb-label">
                <feather-icon icon="CalendarIcon" size="13" class="mr-50" />
                Leaving Date
              </label>
              <flat-pickr
                :config="config"
                v-model="approveObj.leaving_date"
                class="form-control sb-input"
                :placeholder="$t('Select Date')"
              />
            </b-form-group>
          </div>

          <div class="sb-form-field">
            <b-form-group
              label-class="p-0"
              invalid-feedback="Note is required."
              ref="note"
            >
              <label class="sb-label">
                <feather-icon icon="FileTextIcon" size="13" class="mr-50" />
                Note
              </label>
              <b-form-textarea
                placeholder="Write a short approval note..."
                rows="3"
                v-model.trim="approveObj.note"
                class="sb-textarea"
              />
            </b-form-group>
          </div>

          <div class="sb-form-field">
            <label class="sb-label">
              <feather-icon icon="UploadCloudIcon" size="13" class="mr-50" />
              Documents
            </label>
            <div class="d-flex flex-wrap">
              <div
                class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1 bg-white"
                style="width: 110px; height: 96px"
                @click="!docLoading && $refs.reqDocs.click()"
              >
                <b-spinner v-if="docLoading" small variant="primary" />
                <feather-icon
                  v-else
                  icon="PlusIcon"
                  size="34"
                  class="text-primary"
                />
              </div>
              <div
                class="border-dashed d-flex flex-column justify-content-center align-items-center cursor-pointer rounded mr-50 mb-1 bg-white"
                style="width: 110px; height: 96px; position: relative"
                v-for="(file, index) in docList"
                :key="index"
                @click="openFile(file)"
              >
                <b-avatar
                  size="20"
                  variant="primary"
                  style="position: absolute; top: -5px; right: -5px"
                  button
                  @click.stop="removeDoc(index)"
                >
                  <feather-icon icon="XIcon" size="10" />
                </b-avatar>
                <b-img
                  v-if="isImage(file)"
                  :src="getDocPreviewUrl(file)"
                  class="doc-thumb-img"
                  alt="preview"
                  referrerpolicy="no-referrer"
                  @error="onDocPreviewError($event, file)"
                />
                <feather-icon
                  v-else
                  icon="FileTextIcon"
                  size="30"
                  class="text-primary"
                />
              </div>
              <input
                type="file"
                id="reqDocs"
                hidden
                ref="reqDocs"
                multiple
                @change="addDoc()"
                accept="image/*,.pdf"
              />
            </div>
          </div>

          <b-button
            variant="success"
            :disabled="request"
            block
            class="sb-action-btn sb-action-btn--approve mt-2"
            @click="approveReq()"
          >
            <b-spinner v-if="request" small type="grow" class="mr-50" />
            <feather-icon v-else icon="CheckIcon" size="16" class="mr-50" />
            <span>{{
              request ? "Processing..." : sidebarButton || "Approve Request"
            }}</span>
          </b-button>
        </b-form>
      </div>
    </b-sidebar>

    <!-- Sidebar: Reject -->
    <b-sidebar
      id="reject-sidebar"
      v-model="visibility2"
      bg-variant="white"
      backdrop
      shadow
      no-header
      right
      class="resource-sidebar"
    >
      <div class="sb-header sb-header--reject">
        <div class="sb-header__top">
          <div class="sb-header__badge sb-header__badge--danger">
            <feather-icon icon="XCircleIcon" size="14" />
            <span>Reject Request</span>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            class="sb-close-btn"
            @click="visibility2 = false"
          >
            <feather-icon icon="XIcon" size="16" />
          </b-button>
        </div>
        <div class="sb-header__profile">
          <b-avatar
            size="52"
            variant="light-danger"
            :text="sidebarInitials"
            class="sb-avatar"
          />
          <div class="sb-header__info">
            <h5 class="sb-header__name" v-html="sidebarTitle"></h5>
          </div>
        </div>
      </div>

      <div class="sb-body">
        <RequestDetails :reqItem="rejectObj" />

        <div class="sb-divider">
          <span>Action</span>
        </div>

        <b-form @submit.prevent>
          <div class="sb-form-field">
            <b-form-group
              label-class="p-0"
              invalid-feedback="Note is required."
              ref="rej_note"
            >
              <label class="sb-label">
                <feather-icon
                  icon="MessageSquareIcon"
                  size="13"
                  class="mr-50"
                />
                Rejection Reason
              </label>
              <b-form-textarea
                placeholder="Explain why this request is being rejected..."
                rows="4"
                v-model.trim="rejectObj.note"
                class="sb-textarea"
              />
            </b-form-group>
          </div>

          <b-button
            variant="danger"
            :disabled="request"
            block
            class="sb-action-btn sb-action-btn--reject mt-2"
            @click="rejectReq()"
          >
            <b-spinner v-if="request" small type="grow" class="mr-50" />
            <feather-icon v-else icon="SlashIcon" size="16" class="mr-50" />
            <span>{{ request ? "Processing..." : "Reject Request" }}</span>
          </b-button>
        </b-form>
      </div>
    </b-sidebar>

    <!-- Sidebar: Details -->
    <b-sidebar
      id="details-sidebar"
      v-model="visibility3"
      bg-variant="white"
      backdrop
      shadow
      no-header
      right
      class="resource-sidebar"
    >
      <div class="sb-header sb-header--view">
        <div class="sb-header__top">
          <div class="sb-header__badge sb-header__badge--info">
            <feather-icon icon="EyeIcon" size="14" />
            <span>Request Details</span>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            class="sb-close-btn"
            @click="visibility3 = false"
          >
            <feather-icon icon="XIcon" size="16" />
          </b-button>
        </div>
        <div class="sb-header__profile">
          <b-avatar
            size="52"
            variant="light-primary"
            :text="sidebarInitials"
            class="sb-avatar"
          />
          <div class="sb-header__info">
            <h5 class="sb-header__name" v-html="sidebarTitle"></h5>
          </div>
        </div>
      </div>

      <div class="sb-body">
        <RequestDetails v-if="detailObj" :reqItem="detailObj" />

        <div v-if="isReqTypeEdit">
          <div class="sb-form-field">
            <b-form-group
              label-class="p-0"
              :invalid-feedback="$t('Type is required')"
            >
              <label class="sb-label">
                {{ $t("Request Type") }}
              </label>
              <v-select
                v-model="updatedReqType"
                :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                :options="requestOptions.filter((el) => el.value)"
                :reduce="(opt) => opt.value"
                label="text"
                :clearable="false"
                :placeholder="$t('Select type')"
                class="sb-vselect"
              />
            </b-form-group>
          </div>
          <b-button
            variant="success"
            :disabled="updatingReq"
            block
            class="sb-action-btn sb-action-btn--approve mt-2"
            @click="updateRequest()"
          >
            <b-spinner v-if="updatingReq" small type="grow" class="mr-50" />
            <feather-icon v-else icon="CheckIcon" size="16" class="mr-50" />
            <span>{{ updatingReq ? "Updating..." : "Update" }}</span>
          </b-button>
        </div>
      </div>
    </b-sidebar>

    <!-- Sidebar: Transfer Student -->
    <b-sidebar
      id="transfer-sidebar"
      v-model="visibility4"
      bg-variant="white"
      backdrop
      shadow
      no-header
      right
      class="resource-sidebar"
    >
      <div class="sb-header sb-header--view">
        <div class="sb-header__top">
          <div class="sb-header__badge sb-header__badge--info">
            <feather-icon icon="UserPlusIcon" size="14" />
            <span>Transfer Student</span>
          </div>
          <b-button
            variant="flat-secondary"
            size="sm"
            class="sb-close-btn"
            @click="visibility4 = false"
          >
            <feather-icon icon="XIcon" size="16" />
          </b-button>
        </div>
        <div class="sb-header__profile">
          <b-avatar
            size="52"
            variant="light-primary"
            :src="getPicturePreview(chObj.picture)"
            :text="chObj.name ? chObj.name.substring(0, 2).toUpperCase() : 'ST'"
            class="sb-avatar"
            referrerpolicy="no-referrer"
          />
          <div class="sb-header__info">
            <h5 class="sb-header__name">
              {{ chObj.name }}
              <br />
              <small class="text-muted">
                GR:
                {{
                  reportDomain === "myskoolhpgs" ||
                  reportDomain === "myskoolprofectus"
                    ? chObj.grNoLocal
                    : chObj.grNo
                }}
              </small>
            </h5>
          </div>
        </div>
      </div>

      <div class="sb-body">
        <b-form @submit.prevent>
          <div class="sb-divider">
            <span>Transfer Details</span>
          </div>

          <b-row>
            <b-col cols="12" class="mb-2">
              <div class="sb-form-field">
                <label class="sb-label">
                  <feather-icon icon="HomeIcon" size="13" class="mr-50" />
                  Current Campus
                </label>
                <b-badge
                  variant="light-primary"
                  class="font-weight-bold p-1 w-100 text-left"
                  style="border-radius: 10px"
                >
                  {{ getCampus(chObj.campusID) }}
                </b-badge>
              </div>
            </b-col>

            <b-col cols="12">
              <div class="sb-form-field">
                <b-form-group
                  invalid-feedback="Campus is required."
                  ref="ch_camp"
                >
                  <label class="sb-label">
                    <feather-icon icon="MapPinIcon" size="13" class="mr-50" />
                    Transfer to Campus
                  </label>
                  <v-select
                    v-model="chObj.newCampus"
                    :options="campuses"
                    :clearable="false"
                    label="branch"
                    :reduce="(val) => val.id"
                    :disabled="clsLoading"
                    placeholder="Select campus"
                    class="sb-vselect"
                    @input="LoadClass()"
                  />
                </b-form-group>
              </div>
            </b-col>

            <b-col cols="12">
              <div class="sb-form-field">
                <b-form-group
                  :invalid-feedback="$t('Class is required')"
                  ref="ch_cls"
                >
                  <label class="sb-label">
                    <feather-icon icon="BookIcon" size="13" class="mr-50" />
                    Class
                  </label>
                  <v-select
                    v-model="chObj.clsID"
                    :options="chOptions"
                    :clearable="false"
                    label="name"
                    :reduce="(val) => val.id"
                    :disabled="clsLoading"
                    placeholder="Select class"
                    class="sb-vselect"
                    @input="FillChSec()"
                  />
                </b-form-group>
              </div>
            </b-col>

            <b-col cols="12">
              <div class="sb-form-field">
                <b-form-group
                  invalid-feedback="Section is required"
                  ref="ch_sec"
                >
                  <label class="sb-label">
                    <feather-icon icon="GridIcon" size="13" class="mr-50" />
                    Section
                  </label>
                  <v-select
                    v-model="chObj.secID"
                    :options="chSections"
                    :reduce="(opt) => opt.id"
                    label="section"
                    :clearable="false"
                    :disabled="clsLoading"
                    placeholder="Select section"
                    class="sb-vselect"
                    @input="checkChSec()"
                  />
                </b-form-group>
              </div>
            </b-col>

            <b-col v-if="isTcfDomain" cols="12">
              <div class="sb-form-field">
                <b-form-group
                  :invalid-feedback="$t('Scholarship category is required')"
                  ref="ch_scholarship"
                >
                  <label class="sb-label">
                    <feather-icon icon="AwardIcon" size="13" class="mr-50" />
                    Scholarship Category
                  </label>
                  <v-select
                    v-model="chObj.scholarshipCategory"
                    :options="scholarshipCategoryOptions"
                    :clearable="false"
                    placeholder="Select scholarship category"
                    class="sb-vselect"
                    @input="checkChScholarshipCategory()"
                  />
                </b-form-group>
              </div>
            </b-col>

            <b-col cols="12" class="mt-2">
              <b-button
                variant="primary"
                block
                class="sb-action-btn sb-action-btn--view"
                :disabled="request"
                @click="saveCh()"
              >
                <b-spinner v-if="request" small type="grow" class="mr-50" />
                <feather-icon
                  v-else
                  icon="ArrowRightCircleIcon"
                  size="16"
                  class="mr-50"
                />
                <span>{{
                  request ? "Processing..." : "Transfer Student"
                }}</span>
              </b-button>
            </b-col>
          </b-row>
        </b-form>
      </div>
    </b-sidebar>

    <b-card class="all-applications-list-card">
      <div class="gradient-line-wrapper"></div>

      <div
        class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2"
      >
        <div>
          <h5 class="mb-0 resource-title">Student Requests</h5>
          <small class="text-muted"
            >Manage and review student leaving requests</small
          >
        </div>
        <b-button
          v-if="
            rights.add &&
            ['myskooltcfdemo', 'myskooltcf'].includes(reportDomain)
          "
          variant="gradient-primary"
          class="mt-2 mt-md-0"
          @click="openNewRequestSidebar"
        >
          <feather-icon icon="PlusIcon" size="16" class="mr-50" />
          <span>Add request</span>
        </b-button>
      </div>

      <!-- Pagination Controls - Top (Compact Single Line) -->
      <div
        class="pagination-controls-top d-flex justify-content-between align-items-center mb-3"
      >
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <!-- Page Size Dropdown -->
          <div class="page-size-wrapper">
            <label class="page-size-label">{{ $t("Show") }}:</label>
            <v-select
              :key="'page-size-' + perPage"
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
              @option:selected="closePageSizeDropdown"
              ref="pageSizeSelect"
            />
          </div>
        </div>

        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }} <strong>{{ displayedRange.end }}</strong>
            {{ $t("of") }}
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row">
        <!-- Search bar -->
        <b-col
          xl="5"
          lg="5"
          md="6"
          sm="12"
          cols="12"
          class="mt-lg-0 mt-md-0 mt-1"
        >
          <b-input-group class="filter-control search-group">
            <b-input-group-prepend is-text>
              <feather-icon icon="SearchIcon" />
            </b-input-group-prepend>
            <b-form-input
              v-model.trim="searchQuery"
              placeholder="Search by name, GR no..."
            />
          </b-input-group>
        </b-col>

        <!-- From Date -->
        <b-col
          xl="2"
          lg="2"
          md="3"
          sm="6"
          cols="12"
          class="mt-lg-0 mt-md-0 mt-1"
        >
          <b-input-group class="filter-control search-group">
            <b-input-group-prepend is-text>
              <feather-icon icon="CalendarIcon" />
            </b-input-group-prepend>
            <flat-pickr
              v-model="fromDate"
              :config="config"
              class="form-control"
              placeholder="From Date"
              @input="LoadData(currentStatus)"
            />
          </b-input-group>
        </b-col>

        <!-- To Date -->
        <b-col
          xl="2"
          lg="2"
          md="3"
          sm="6"
          cols="12"
          class="mt-lg-0 mt-md-0 mt-1"
        >
          <b-input-group class="filter-control search-group">
            <b-input-group-prepend is-text>
              <feather-icon icon="CalendarIcon" />
            </b-input-group-prepend>
            <flat-pickr
              v-model="toDate"
              :config="config"
              class="form-control"
              placeholder="To Date"
              @input="LoadData(currentStatus)"
            />
          </b-input-group>
        </b-col>

        <!-- Status Dropdown & Action Buttons -->
        <b-col
          xl="3"
          lg="3"
          md="12"
          sm="12"
          cols="12"
          class="mt-lg-0 mt-1 d-flex align-items-center"
          style="gap: 10px"
        >
          <v-select
            v-model="currentStatus"
            :options="statusOptions"
            :reduce="(option) => option.value"
            label="text"
            :clearable="false"
            class="filter-control campus-select"
            style="min-width: 140px"
            placeholder="Select Status"
            @input="LoadData($event)"
          />

          <v-select
            v-model="currentType"
            :options="requestOptions"
            :reduce="(option) => option.value"
            label="text"
            :clearable="false"
            class="filter-control campus-select"
            style="min-width: 140px"
            placeholder="Select type"
            @input="LoadData()"
          />
        </b-col>
      </b-row>
      <b-row class="mb-2 mt-25">
        <b-col xl="3" lg="3" md="6" sm="12" cols="12">
          <v-select
            v-model="requestedBy"
            :options="userOptions"
            :clearable="true"
            class="filter-control campus-select"
            style="min-width: 140px"
            placeholder="Select requested by"
            @input="LoadData()"
          />
        </b-col>
        <b-col
          xl="9"
          lg="9"
          md="6"
          class="d-flex align-items-center justify-content-end"
          style="gap: 10px"
        >
          <!-- Export Button -->
          <b-button
            variant="outline-primary"
            class="action-btn-outline"
            @click="exportData"
            :disabled="exporting"
            v-b-tooltip.hover.top="'Export to Excel'"
            style="
              height: 46px;
              width: 46px;
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 0;
            "
          >
            <feather-icon icon="FileTextIcon" size="18" />
          </b-button>

          <!-- Print Button -->
          <b-button
            variant="outline-primary"
            class="action-btn-outline"
            @click="printReport"
            :disabled="dataLoading"
            v-b-tooltip.hover.top="'Print Report'"
            style="
              height: 46px;
              width: 46px;
              border-radius: 12px;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 0;
            "
          >
            <feather-icon icon="PrinterIcon" size="18" />
          </b-button>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <!-- Top Scrollbar for PC -->
        <div class="table-scrollbar-top" ref="topScrollbar">
          <div class="table-scrollbar-content"></div>
        </div>

        <!-- Table Wrapper -->
        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="all-applications-table mt-1"
            ref="selectableTable"
            :items="paginatedItems"
            :fields="fields"
            :busy="dataLoading"
            show-empty
            :responsive="false"
            hover
            small
          >
            <template #empty="scope">
              <h3 class="mt-1 mb-1 text-center">No records found</h3>
            </template>

            <template #table-busy>
              <tbody>
                <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
                  <td
                    v-for="(field, index) in fields"
                    :key="'skeleton-cell-' + index"
                    class="skeleton-cell"
                  >
                    <div class="skeleton-loader"></div>
                  </td>
                </tr>
              </tbody>
            </template>

            <template #cell(index)="data">
              {{ (currentPage - 1) * perPage + data.index + 1 }}
            </template>

            <template #cell(name)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="44"
                    :src="getPicturePreview(data.item.st.picture)"
                    variant="light-primary"
                    referrerpolicy="no-referrer"
                  />
                </template>

                <b-link
                  class="font-weight-bold d-block text-nowrap"
                  style="margin-bottom: 4px"
                >
                  <b-badge variant="light-primary">
                    {{ data.item.st.name }}
                  </b-badge>
                </b-link>
                <template v-if="data.item.st.id > 0">
                  <template
                    v-if="
                      ['myskoolhpgs', 'myskoolprofectus'].includes(reportDomain)
                    "
                  >
                    <small class="text-muted">
                      <b-badge variant="light-primary" class="mr-50">
                        GR - {{ data.item.st.grNoLocal }}
                      </b-badge>
                    </small>
                    <b-link class="font-weight-bold d-block text-nowrap mt-25">
                      <b-badge variant="light-primary">
                        CID: {{ data.item.st.grNo }}
                      </b-badge>
                    </b-link>
                  </template>
                  <template
                    v-if="
                      ['myskooltcfdemo', 'myskooltcf'].includes(reportDomain)
                    "
                  >
                    <small class="text-muted">
                      <b-badge variant="light-primary" class="mr-50">
                        FB Reg. No.
                        {{
                          data.item.stdDetails
                            ? data.item.stdDetails.federalRegNo || "—"
                            : "—"
                        }}
                      </b-badge>
                    </small>
                    <b-link
                      v-if="data.item.st.meta_class"
                      class="font-weight-bold d-block text-nowrap mt-25"
                    >
                      <b-badge variant="light-primary">
                        {{ data.item.st.meta_class }}
                      </b-badge>
                    </b-link>
                  </template>
                  <template v-else>
                    <small class="text-muted">
                      <b-badge variant="light-primary" class="mr-50">
                        GR - {{ data.item.st.grNo }}
                      </b-badge>
                    </small>
                    <b-link class="font-weight-bold d-block text-nowrap mt-25">
                      <b-badge variant="light-primary">
                        CID: {{ data.item.st.grNo }}
                      </b-badge>
                    </b-link>
                  </template>
                  <div>
                    <b-badge variant="light-primary" class="mt-25">
                      {{ getCampus(data.item.st.campusID) }}
                    </b-badge>
                  </div>
                </template>
              </b-media>
            </template>
            <template #cell(campus)="data">
              <b-badge variant="light-primary">
                {{ getCampus(data.item.us.campusID) }}
              </b-badge>
            </template>
            <template #cell(request_type)="data">
              <b-badge variant="light-primary">
                {{ data.item.us.type }}
              </b-badge>
            </template>
            <template #cell(father_name)="data">
              <b-badge variant="light-primary">
                {{ data.item.st.father_name }}
              </b-badge>
            </template>
            <template #cell(contact_father)="data">
              <b-badge variant="light-primary">
                {{ data.item.st.contact_father }}
              </b-badge>
            </template>
            <template #cell(class_current)="data">
              <b-badge variant="light-primary">
                {{ data.item.st.meta_class }}
              </b-badge>
            </template>
            <template #cell(requested_by)="{ item }">
              <b-badge variant="light-primary" class="pill-badge-user">
                <feather-icon icon="UserIcon" size="12" class="mr-25" />
                <span>{{
                  item.us
                    ? item.us.requested_by == 0
                      ? "Student"
                      : item.us.meta_requested_by || "-"
                    : "-"
                }}</span>
              </b-badge>
            </template>

            <template #cell(approved_by)="{ item }">
              <div
                v-if="
                  item.us &&
                  (item.us.status === 'accepted' ||
                    item.us.status === 'rejected')
                "
              >
                <b-badge variant="light-success" class="pill-badge-user">
                  <feather-icon icon="ShieldIcon" size="12" class="mr-25" />
                  <span>{{ item.us.meta_approved_by || "-" }}</span>
                </b-badge>
              </div>
              <b-badge v-else variant="light-secondary" class="pill-badge-user">
                <feather-icon icon="ClockIcon" size="12" class="mr-25" />
                <span>Pending Action</span>
              </b-badge>
            </template>

            <template #cell(status)="{ item }">
              <b-badge variant="light-primary">
                {{ item.us.status }}
              </b-badge>
            </template>

            <template #head(actions)="data">
              <div class="text-center">
                <span>{{ data.label }}</span>
              </div>
            </template>

            <template #cell(actions)="data">
              <div class="d-flex justify-content-center">
                <b-button
                  variant="gradient-primary"
                  class="action-btn-table"
                  :class="data.item.us.status == 'pending' ? 'mr-1' : ''"
                  v-b-tooltip.hover.top
                  title="View"
                  @click="openDetails(data.item.us, data.item.st, false)"
                >
                  <feather-icon icon="EyeIcon" />
                </b-button>
                <template
                  v-if="
                    rights.edit &&
                    data.item.us.status == 'pending' &&
                    (['myskooltcfdemo', 'myskooltcf'].includes(reportDomain)
                      ? isHeadOffice
                      : true)
                  "
                >
                  <b-button
                    v-if="isTcfDomain"
                    variant="outline-primary"
                    class="action-btn-outline mr-1"
                    v-b-tooltip.hover.top
                    title="Edit Request Type"
                    @click="openDetails(data.item.us, data.item.st, true)"
                  >
                    <feather-icon icon="EditIcon" />
                  </b-button>
                  <b-button
                    variant="outline-primary"
                    class="action-btn-outline mr-1"
                    v-b-tooltip.hover.top
                    title="Approve"
                    @click="openApprove(data.item.us, data.item.st)"
                  >
                    <feather-icon icon="CheckCircleIcon" />
                  </b-button>
                  <b-button
                    variant="outline-danger"
                    class="action-btn-outline delete-btn"
                    v-b-tooltip.hover.top
                    title="Reject"
                    @click="openReject(data.item.us, data.item.st)"
                  >
                    <feather-icon icon="XIcon" />
                  </b-button>
                </template>
              </div>
            </template>
          </b-table>
        </div>
      </div>
    </b-card>
  </div>
</template>
<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BMedia,
  BAvatar,
  // BLink,
  BBadge,
  BDropdown,
  BDropdownItem,
  BPagination,
  BImg,
  VBTooltip,
  BFormCheckbox,
  BFormGroup,
  BForm,
  BContainer,
  BMediaAside,
  BSpinner,
  BCardTitle,
  BCardText,
  BSidebar,
  VBToggle,
  BInputGroupPrepend,
  BInputGroup,
  BInputGroupAppend,
  BFormTextarea,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions, mapGetters } from "vuex";
import {
  getUploadedPaths,
  uploadToMySkool,
  getMediaPreviewUrl,
  handleDrivePreviewError,
  isImageUrl,
} from "@/utils/gdriveUpload";
import RequestDetails from "./RequestDetails.vue";
import _ from "lodash";

export default {
  components: {
    RequestDetails,
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BMedia,
    BAvatar,
    // BLink,
    BBadge,
    BDropdown,
    BDropdownItem,
    BPagination,
    BImg,
    BFormCheckbox,
    BFormGroup,
    BForm,
    BContainer,
    BMediaAside,
    BSpinner,
    BCardTitle,
    BCardText,
    BSidebar,
    BInputGroupPrepend,
    BInputGroup,
    BInputGroupAppend,
    BFormTextarea,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
    "b-toggle": VBToggle,
  },
  data() {
    return {
      config: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      rights: {},
      statusOptions: [
        { text: "All", value: "all" },
        { text: "Pending", value: "pending" },
        { text: "Accepted", value: "accepted" },
        { text: "Rejected", value: "rejected" },
      ],
      campuses: [],
      dataLoading: false,
      currentStatus: "pending",
      items: [],
      totalRecords: 0,
      totalPages: 0,
      currentPage: 1,
      perPage: 50,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
        { label: "200", value: 200 },
      ],
      searchQuery: "",
      fromDate: "",
      toDate: "",
      exporting: false,
      fields: [
        {
          label: "S.No",
          key: "index",
          thClass: "sticky-sno-2",
          tdClass: "sticky-sno-2",
        },
        {
          label: "Name",
          key: "name",
          thClass: "sticky-name",
          tdClass: "sticky-name",
        },
        { label: "Father Name", key: "father_name" },
        { label: "Type", key: "request_type" },
        { label: "Requested By", key: "requested_by" },
        { label: "Action By", key: "approved_by" },
        { label: "Contact", key: "contact_father" },
        { label: "Status", key: "status" },
        "actions",
      ],
      request: false,
      sidebarTitle: "",
      sidebarButton: "",
      visibility: false,
      approveObj: {},
      docLoading: false,
      docList: [],
      visibility2: false,
      rejectObj: {},
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      visibility3: false,
      isReqTypeEdit: false,
      updatedReqType: "",
      updatingReq: false,
      detailObj: null,
      chObj: {},
      visibility4: false,
      chOptions: [],
      chClasses: [],
      chSections: [],
      clsLoading: false,
      currentStd: {},
      visibility5: false,
      newRequestStep: "menu",
      federalBoardQuery: "",
      federalBoardSearchAttempted: false,
      studentSearchLoading: false,
      studentSearchResults: [],
      selectedStudentForRequest: null,
      newAdmissionMode: false,
      newAdmissionReasonSeparator: " || ",
      newAdmissionTelDropdownOptions: {
        disabled: false,
        showDialCodeInList: true,
        showFlags: true,
      },
      newAdmissionTelInputOptions: {
        student: { placeholder: "Student Contact No" },
        whatsapp: { placeholder: "WhatsApp No" },
        parent: { placeholder: "Parent's Contact" },
      },
      newAdmissionForm: {
        federal_board_reg_no: "",
        matric_roll_no: "",
        student_name: "",
        father_name: "",
        student_contact: "",
        whatsapp_no: "",
        parent_contact: "",
        note: "",
      },
      reqObj: {},
      newReqDocList: [],
      newReqDocLoading: false,
      savingNewRequest: false,
      newAdmissionWhatsappPhoneError: false,
      isHeadOffice: null,
      scholarshipCategoryOptions: [
        "Offered Coaching",
        "Offered Pvt College",
        "Offered TCF College",
        "Not Offered",
      ],
      currentType: "",
      requestOptions: [
        { text: "All", value: "" },
        { text: "General", value: "general" },
        { text: "Left", value: "left" },
        { text: "TC", value: "tc" },
        { text: "Transfer", value: "transfer" },
      ],
      requestedBy: "",
      userOptions: [],
    };
  },
  computed: {
    isTcfDomain() {
      return ["myskooltcfdemo", "myskooltcf"].includes(this.reportDomain);
    },
    filteredItems() {
      // With server-side search enabled, we just return the items from the API
      return this.items;
    },
    totalCount() {
      return this.totalRecords;
    },
    paginatedItems() {
      // With server-side pagination, the items from the API are already the current page
      return this.items;
    },
    sidebarInitials() {
      const cleanText = this.sidebarTitle.replace(/<[^>]*>/g, " ").trim();
      const parts = cleanText.split(/[\s\-]+/).filter(Boolean);
      if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
      if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
      return "ST";
    },
    displayedRange() {
      if (this.totalRecords === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalRecords);
      return { start: Math.max(0, start), end: Math.max(0, end) };
    },
    leaveReqTypeOptions() {
      if (this.newAdmissionMode) {
        return [{ text: "New Admission", value: "new-admission" }];
      }
      const base = [{ text: "General", value: "general" }];
      const st = this.selectedStudentForRequest;
      if (
        st &&
        st.status &&
        ["present", "registered"].includes(String(st.status).toLowerCase())
      ) {
        return [
          ...base,
          { text: "Left", value: "left" },
          { text: "TC", value: "tc" },
          { text: "Transfer", value: "transfer" },
        ];
      }
      return base;
    },
  },
  watch: {
    searchQuery() {
      this.currentPage = 1;
      this.debouncedSearch();
    },
    visibility5(val) {
      if (!val) this.resetNewRequestSidebar();
    },
  },
  mounted() {
    this.updateScrollbarWidth();
    window.addEventListener("resize", this.updateScrollbarWidth);
  },
  updated() {
    this.updateScrollbarWidth();
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.updateScrollbarWidth);
  },
  created() {
    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      this.LoadSettings();
      this.LoadCampuses();
      this.LoadData("pending");
      this.LoadReqUsers();
    }
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    debouncedSearch: _.debounce(function () {
      this.LoadData(this.currentStatus);
    }, 500),

    onPageChange(page) {
      this.currentPage = page;
      this.LoadData();
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.LoadData();
      this.$nextTick(() => {
        this.updateScrollbarWidth();
      });
    },

    closePageSizeDropdown() {
      this.$nextTick(() => {
        if (this.$refs.pageSizeSelect) {
          const toggle = this.$refs.pageSizeSelect.$el?.querySelector(
            ".vs__dropdown-toggle"
          );
          if (toggle) toggle.blur();
        }
      });
    },

    syncScroll() {
      if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
        const tableWrapper = this.$refs.tableWrapper;
        const topScrollbar = this.$refs.topScrollbar;
        const scrollContent = topScrollbar.querySelector(
          ".table-scrollbar-content"
        );

        if (scrollContent) {
          topScrollbar.scrollLeft = tableWrapper.scrollLeft;
        }
      }
    },

    updateScrollbarWidth() {
      this.$nextTick(() => {
        if (this.$refs.tableWrapper && this.$refs.topScrollbar) {
          const tableWrapper = this.$refs.tableWrapper;
          const topScrollbar = this.$refs.topScrollbar;
          const scrollContent = topScrollbar.querySelector(
            ".table-scrollbar-content"
          );

          if (scrollContent) {
            if (tableWrapper.scrollWidth > tableWrapper.clientWidth) {
              scrollContent.style.width = `${tableWrapper.scrollWidth}px`;
              topScrollbar.style.display = "block";

              topScrollbar.onscroll = () => {
                tableWrapper.scrollLeft = topScrollbar.scrollLeft;
              };
            } else {
              topScrollbar.style.display = "none";
            }
          }
        }
      });
    },

    isImage(url) {
      return isImageUrl(url);
    },
    getDocPreviewUrl(url) {
      return getMediaPreviewUrl(url, "Image");
    },
    onDocPreviewError(event, url) {
      if (!url) return;
      handleDrivePreviewError(event, url, "image");
    },
    getPicturePreview(url) {
      if (!url) return "";
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
    openFile(url) {
      const target = isImageUrl(url) ? getMediaPreviewUrl(url, "Image") : url;
      window.open(target, "_blank");
    },
    getCampus(id) {
      let obj = this.campuses.find((el) => el.id == id);
      return obj ? obj.branch : "";
    },

    openApprove(item, std) {
      this.approveObj = { ...item };
      this.currentStd = { ...std };
      if (item.approved_document)
        this.docList = item.approved_document.split(",");
      else this.docList = [];

      let text = "";
      if (["myskoolhpgs", "myskoolprofectus"].includes(this.reportDomain)) {
        text = `Gr: ${std.grNoLocal} CID: ${std.grNo}`;
      } else {
        if (std.id > 0) text = `Gr: ${std.grNo} ID: ${std.id}`;
      }
      this.sidebarTitle = `${std.name}<br/>${text}`;
      this.sidebarButton =
        item.type == "transfer" ? "Approve & Transfer" : "Approve";
      this.visibility = true;
    },
    async addDoc() {
      if (this.$refs.reqDocs.files.length > 0) {
        this.docLoading = true;
        try {
          const uploaded = await uploadToMySkool(this.$refs.reqDocs.files);
          getUploadedPaths(uploaded).forEach((path) => {
            this.docList.push(path);
          });
          this.approveObj.approved_document = this.docList.join(",");
        } catch (err) {
          console.log(err);
        } finally {
          this.docLoading = false;
        }
      }
    },
    removeDoc(index) {
      this.docList.splice(index, 1);
      this.approveObj.approved_document = this.docList.join(",");
    },
    CheckDate() {
      var elem = this.$refs["date"];
      if (!this.approveObj.leaving_date) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    CheckNote() {
      var elem = this.$refs["note"];
      if (this.approveObj.note.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async approveReq() {
      if (this.CheckDate() == true && this.CheckNote() == true) {
        this.approveObj.status = "accepted";
        this.approveObj.approved_by = this.$store.state.userData.userID;
        //  console.log(this.approveObj)
        this.request = true;
        var status = await this.post({
          url:
            this.$store.state.domain +
            "studentLeftRequest/ApproveRequest?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.approveObj.campusID,
          body: this.approveObj,
          message: `Student marked as left!`,
          context: this,
          token: this.$store.state.userData.token,
        });
        this.request = false;
        if (status) {
          this.LoadData(this.currentStatus);
          this.visibility = false;
          if (this.approveObj.type == "transfer") this.openCh(this.currentStd);
        }
      }
    },
    openReject(item, std) {
      this.rejectObj = { ...item };

      let text = "";
      if (["myskoolhpgs", "myskoolprofectus"].includes(this.reportDomain)) {
        text = `Gr: ${std.grNoLocal} CID: ${std.grNo}`;
      } else {
        if (std.id > 0) text = `Gr: ${std.grNo} ID: ${std.id}`;
      }
      this.sidebarTitle = `${std.name}<br/>${text}`;
      this.visibility2 = true;
    },
    CheckReason() {
      var elem = this.$refs["rej_note"];
      if (this.rejectObj.note.trim() == "") {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    async rejectReq() {
      if (this.CheckReason() == true) {
        this.rejectObj.status = "rejected";
        this.rejectObj.leaving_date = new Date().toJSON();
        this.rejectObj.approved_by = this.$store.state.userData.userID;

        // console.log(this.rejectObj);

        this.request = true;
        var status = await this.post({
          url:
            this.$store.state.domain +
            "studentLeftRequest/ApproveRequest?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.rejectObj.campusID,
          body: this.rejectObj,
          message: `Request rejected successfully!`,
          context: this,
          token: this.$store.state.userData.token,
        });
        this.request = false;
        if (status) {
          this.LoadData(this.currentStatus);
          this.visibility2 = false;
        }
      }
    },
    openDetails(item, std, isEdit) {
      this.detailObj = { ...item };
      this.isReqTypeEdit = isEdit;
      this.updatedReqType = item.type;

      let text = "";
      if (["myskoolhpgs", "myskoolprofectus"].includes(this.reportDomain)) {
        text = `Gr: ${std.grNoLocal} CID: ${std.grNo}`;
      } else {
        if (std.id > 0) text = `Gr: ${std.grNo} ID: ${std.id}`;
      }
      this.sidebarTitle = `${std.name}<br/>${text}`;
      this.visibility3 = true;
    },
    async updateRequest() {
      this.updatingReq = true;
      let payload = { ...this.detailObj };
      payload.type = this.updatedReqType;

      var status = await this.put({
        url:
          this.$store.state.domain +
          "studentLeftRequest/" +
          payload.id +
          "?db=" +
          this.$store.state.userData.db,
        body: payload,
        message: "Request updated successfully!",
        context: this,
        token: this.$store.state.userData.token,
      });
      this.updatingReq = false;
      if (status) {
        this.visibility3 = false;
        this.LoadData(this.currentStatus);
      }
    },

    async openCh(item) {
      this.chObj = {
        ...item,
        newCampus: 0,
        clsID: 0,
        secID: 0,
        scholarshipCategory: item.scholarshipCategory || "",
      };

      this.visibility4 = true;
      this.chSections = [];
      this.chClasses = [];
      this.chOptions = [];

      if (this.isTcfDomain && item.id) {
        await this.loadTransferScholarshipCategory(item.id);
      }
    },
    async loadTransferScholarshipCategory(sID) {
      try {
        const res = await this.get({
          url:
            this.$store.state.domain +
            "StudentDetails/GetByStudentID?db=" +
            this.$store.state.userData.db +
            "&sID=" +
            sID,
          token: this.$store.state.userData.token,
        });
        const category =
          res && res.studentDetails && res.studentDetails.scholarshipCategory;
        if (category) {
          this.chObj.scholarshipCategory = category;
        }
      } catch (e) {
        console.error("Load transfer scholarship category error:", e);
      }
    },
    async LoadClass() {
      this.checkChCamp();
      this.clsLoading = true;
      this.chObj.clsID = 0;
      this.chObj.secID = 0;
      this.chSections = [];

      var obj = {
        url:
          this.$store.state.domain +
          "myclasses/LoadClasses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.chObj.newCampus +
          "&dID=0",
        token: this.$store.state.userData.token,
      };
      //  this.items =[];
      let myitem = await this.get(obj);
      this.chClasses = myitem;
      this.chOptions = this.chClasses.map((el) => el.cls);

      this.clsLoading = false;
    },
    FillChSec() {
      this.chObj.secID = 0;
      this.chSections = [];
      let obj = this.chClasses.find((el) => el.cls.id == this.chObj.clsID);
      if (obj && obj.sections.length > 0) {
        this.chSections = obj.sections;
      } else this.chSections = [];

      this.checkChClass();
    },
    checkChCamp() {
      var elem = this.$refs["ch_camp"];
      if (this.chObj.newCampus == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkChClass() {
      var elem = this.$refs["ch_cls"];
      if (this.chObj.clsID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkChSec() {
      var elem = this.$refs["ch_sec"];
      if (this.chObj.secID == 0) {
        return (elem.state = false);
      } else {
        return (elem.state = true);
      }
    },
    checkChScholarshipCategory() {
      if (!this.isTcfDomain) return true;
      var elem = this.$refs["ch_scholarship"];
      if (!elem) return true;
      if (!this.chObj.scholarshipCategory) {
        return (elem.state = false);
      }
      return (elem.state = true);
    },
    async saveCh() {
      this.checkChCamp();
      this.checkChClass();
      this.checkChSec();
      if (this.isTcfDomain) {
        this.checkChScholarshipCategory();
      }
      if (
        this.checkChCamp() == false ||
        this.checkChClass() == false ||
        this.checkChSec() == false ||
        (this.isTcfDomain && this.checkChScholarshipCategory() == false)
      ) {
        this.$bvToast.toast(this.$t("Please select all the required fields"), {
          title: this.$t("Error!"),
          variant: "danger",
          toaster: "b-toaster-top-center",
        });
      }
      // else if (this.chObj.campusID == this.chObj.newCampus) {
      //   this.$bvToast.toast("Current campus and transfer campus are same", {
      //     title: this.$t("Error!"),
      //     variant: "danger",
      //     toaster: "b-toaster-top-center",
      //   });
      // }
      else {
        let result = await Swal.fire({
          title: this.$t("Are you sure?"),
          text: "Do you want to transfer the student?",
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#d33",
          cancelButtonColor: "#3085d6",
          confirmButtonText: "Yes!",
        });

        if (result.isConfirmed) {
          this.request = true;
          let data = {
            sID: this.chObj.id,
            previousCampus: this.chObj.campusID,
            newCampus: this.chObj.newCampus,
            clsID: this.chObj.clsID,
            secID: this.chObj.secID,
            userID: this.$store.state.userData.userID,
          };
          if (this.isTcfDomain) {
            data.scholarshipCategory = this.chObj.scholarshipCategory;
          }
          // console.log(data);
          let status = await this.post({
            url:
              this.$store.state.domain +
              "Students/TransferCampus?db=" +
              this.$store.state.userData.db,
            body: data,
            message: "Student transferred successfully!",
            context: this,
            token: this.$store.state.userData.token,
          });
          this.request = false;
          if (status) {
            this.visibility4 = false;
            this.LoadData(this.currentStatus);
          }
        }
      }
    },
    printReport() {
      const url = this.$router.resolve({
        name: "leaving-request-print",
        query: {
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          fromDate: this.fromDate,
          toDate: this.toDate,
          status: this.currentStatus,
          search: this.searchQuery,
          type: this.currentType,
          requestedBy: this.requestedBy,
        },
      }).href;
      window.open(url, "_blank");
    },
    async exportData() {
      this.exporting = true;
      try {
        const url =
          this.$store.state.domain +
          "studentLeftRequest/LoadLeftRequestReport?";
        const params = new URLSearchParams();
        params.append("db", this.$store.state.userData.db);
        params.append("cID", this.$store.state.userData.cId);
        params.append("fromDate", this.fromDate || "");
        params.append("toDate", this.toDate || "");
        params.append("status", this.currentStatus || "all");
        params.append("search", this.searchQuery || "");
        params.append("type", this.currentType || "");
        params.append("requestedBy", this.requestedBy || "");

        const res = await this.get({
          url: url + params.toString(),
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        let reportItems = [];
        if (res && res.data && Array.isArray(res.data)) {
          reportItems = res.data;
        } else if (res && Array.isArray(res.data)) {
          reportItems = res.data;
        } else if (res && Array.isArray(res)) {
          reportItems = res;
        } else if (
          res &&
          res.data &&
          res.data.data &&
          Array.isArray(res.data.data)
        ) {
          reportItems = res.data.data;
        }

        if (!reportItems || reportItems.length === 0) {
          this.$bvToast.toast("No records found to export", {
            variant: "warning",
            solid: true,
            title: "Export Failed",
          });
          this.exporting = false;
          return;
        }

        const dataToExport = reportItems.map((item, index) => {
          const st = item.student || item.st;
          const us = item.request || item.us;
          const reportDomain = this.$store.state.report.replace(/_/g, "");

          return {
            "S.No": index + 1,
            Name: st ? st.name : "-",
            "GR No": st
              ? ["myskoolhpgs", "myskoolprofectus"].includes(reportDomain)
                ? st.grNoLocal
                : st.grNo
              : "-",
            "Father Name": st ? st.father_name : "-",
            Class: st ? st.meta_class : "-",
            "Requested By": us ? us.meta_requested_by || "-" : "-",
            "Action By": us ? us.meta_approved_by || "-" : "-",
            "Req Date":
              us && us.request_date
                ? new Date(us.request_date).toLocaleDateString("en-GB")
                : "-",
            "Leaving Date":
              us && us.leaving_date
                ? new Date(us.leaving_date).toLocaleDateString("en-GB")
                : "-",
            Status: us && us.status ? us.status.toUpperCase() : "PENDING",
            "Reason / Details": us ? us.reason : "-",
          };
        });

        const XLSX = require("xlsx");
        const ws = XLSX.utils.json_to_sheet(dataToExport);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Leaving Requests");
        XLSX.writeFile(
          wb,
          `Student_Leaving_Requests_${new Date().getTime()}.xlsx`
        );

        this.$bvToast.toast(
          `Successfully exported ${reportItems.length} records.`,
          {
            variant: "success",
            solid: true,
            title: "Export Successful",
          }
        );
      } catch (error) {
        console.error("Export Error:", error);
        this.$bvToast.toast("An error occurred while exporting data.", {
          variant: "danger",
          solid: true,
          title: "Error",
        });
      } finally {
        this.exporting = false;
      }
    },
    async LoadData(st) {
      if (st) {
        this.currentStatus = st;
        this.currentPage = 1;
      }
      this.dataLoading = true;
      try {
        let url = this.$store.state.domain + "studentLeftRequest/LoadData?";
        const params = new URLSearchParams();
        params.append("db", this.$store.state.userData.db);
        params.append("cID", this.$store.state.userData.cId);
        params.append("status", this.currentStatus || "all");
        params.append("type", this.currentType || "");
        params.append("requestedBy", this.requestedBy || "");
        params.append("page", this.currentPage);
        params.append("pageSize", this.perPage);

        if (this.searchQuery && this.searchQuery.trim() !== "") {
          params.append("search", this.searchQuery.trim());
        }
        if (this.fromDate) {
          params.append("fromDate", this.fromDate);
        }
        if (this.toDate) {
          params.append("toDate", this.toDate);
        }

        url += params.toString();

        const res = await this.get({
          url,
          token: this.$store.state.userData.token,
          dashboard: true,
        });

        let items = [];
        let totalRecords = 0;
        let totalPages = 0;

        if (res && res.data && res.data.data) {
          // Format: { status: "success", data: { data: [...], totalCount: 123 } }
          items = Array.isArray(res.data.data) ? res.data.data : [];
          totalRecords =
            res.data.totalCount || res.data.totalRecords || items.length;
          totalPages =
            res.data.totalPages || Math.ceil(totalRecords / this.perPage);
        } else if (res && res.data && Array.isArray(res.data)) {
          // Format: { status: "success", data: [...], totalCount: 123 }
          items = res.data;
          totalRecords = res.totalCount || res.totalRecords || items.length;
          totalPages = res.totalPages || Math.ceil(totalRecords / this.perPage);
        } else if (res && Array.isArray(res)) {
          // Format: [...]
          items = res;
          totalRecords = items.length;
          totalPages = Math.ceil(totalRecords / this.perPage);
        } else {
          items = [];
          totalRecords = 0;
          totalPages = 0;
        }

        this.items = items.map((item) => ({
          ...item,
          st: item.student || item.st,
          us: item.request || item.us,
        }));

        this.totalRecords = totalRecords || 0;
        this.totalPages =
          totalPages || Math.ceil(this.totalRecords / this.perPage) || 1;
      } catch (error) {
        console.error("Error loading data:", error);
        this.items = [];
        this.totalRecords = 0;
        this.totalPages = 0;
      } finally {
        this.dataLoading = false;
      }
    },

    async LoadSettings() {
      let res = await this.post({
        url:
          this.$store.state.domain +
          "Settings/getSettings?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        body: ["head_office"],
        context: this,
        token: this.$store.state.userData.token,
        subjects: true,
      });

      this.isHeadOffice = res.find((el) => el.key == "head_office");
      if (this.isHeadOffice || ["myskoolhpgs"].includes(this.reportDomain)) {
        this.fields.splice(3, 0, { label: "campus", key: "campus" });
      }
    },
    async LoadCampuses() {
      var obj = {
        url:
          this.$store.state.domain +
          "campuses?db=" +
          this.$store.state.userData.db +
          "&cId=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.campuses = await this.get(obj);
    },
    async LoadReqUsers() {
      var obj = {
        url:
          this.$store.state.domain +
          "studentLeftRequest/GetUserList?db=" +
          this.$store.state.userData.db +
          "&cID=" +
          this.$store.state.userData.cId,
        token: this.$store.state.userData.token,
      };
      this.userOptions = await this.get(obj);
      this.userOptions.unshift("Student");
    },

    resetNewRequestSidebar() {
      this.newRequestStep = "menu";
      this.federalBoardQuery = "";
      this.studentSearchResults = [];
      this.federalBoardSearchAttempted = false;
      this.studentSearchLoading = false;
      this.selectedStudentForRequest = null;
      this.newAdmissionMode = false;
      this.resetNewAdmissionFormState();
      this.newReqDocList = [];
      this.reqObj = {};
      this.newReqDocLoading = false;
      this.savingNewRequest = false;
      this.sidebarTitle = "";
      this.clearNewReqFileInput();
    },

    clearNewReqFileInput() {
      this.$nextTick(() => {
        const el = this.$refs.newLeaveReqDocsInput;
        if (el) el.value = "";
      });
    },

    openNewRequestSidebar() {
      this.resetNewRequestSidebar();
      this.sidebarTitle = "New Request";
      this.visibility5 = true;
    },

    beginSearchStudentFlow() {
      this.newAdmissionMode = false;
      this.federalBoardQuery = "";
      this.studentSearchResults = [];
      this.federalBoardSearchAttempted = false;
      this.newRequestStep = "search";
      this.sidebarTitle = "Search student";
    },

    backToNewRequestMenu() {
      this.newRequestStep = "menu";
      this.federalBoardQuery = "";
      this.studentSearchResults = [];
      this.federalBoardSearchAttempted = false;
      this.sidebarTitle = "New Request";
    },

    async beginNewAdmissionFlow() {
      this.newAdmissionMode = true;
      this.selectedStudentForRequest = null;
      this.initNewAdmissionReqPayload();
      this.newRequestStep = "form";
      this.sidebarTitle = "New admission request";
      await this.$nextTick();
      this.resetNewReqValidationRefs();
    },

    resetNewAdmissionFormState() {
      this.newAdmissionWhatsappPhoneError = false;
      this.newAdmissionForm = {
        federal_board_reg_no: "",
        matric_roll_no: "",
        student_name: "",
        father_name: "",
        student_contact: "",
        whatsapp_no: "",
        parent_contact: "",
        note: "",
      };
    },

    buildNewAdmissionReasonFromForm() {
      const n = this.newAdmissionForm;
      const sep = this.newAdmissionReasonSeparator;
      const parts = [
        `Federal Board Registration No: ${(
          n.federal_board_reg_no || ""
        ).trim()}`,
        `Matric Roll No: ${(n.matric_roll_no || "").trim()}`,
        `Student Name: ${(n.student_name || "").trim()}`,
        `Father Name: ${(n.father_name || "").trim()}`,
        `WhatsApp No: ${String(n.whatsapp_no || "").trim()}`,
        `Student Contact No: ${String(n.student_contact || "").trim()}`,
        `Parent's Contact: ${String(n.parent_contact || "").trim()}`,
        `Note: ${(n.note || "").trim()}`,
      ];
      return parts.join(sep);
    },

    initNewAdmissionReqPayload() {
      this.resetNewAdmissionFormState();
      this.newReqDocList = [];
      this.reqObj = {
        id: 0,
        sID: 0,
        request_date: new Date().toJSON(),
        reason: "",
        leaving_date: null,
        note: "",
        document: "",
        status: "pending",
        requested_by: this.$store.state.userData.userID,
        campusID: this.$store.state.userData.cId,
        type: "new-admission",
        approved_by: 0,
        approved_document: "",
      };
      this.clearNewReqFileInput();
    },

    checkNewAdmissionWhatsapp(s, e) {
      if (this.newAdmissionForm.whatsapp_no) {
        if (e.valid) {
          this.newAdmissionWhatsappPhoneError = false;
          this.newAdmissionForm.whatsapp_no = e.number;
        } else {
          this.newAdmissionWhatsappPhoneError = true;
        }
      } else {
        this.newAdmissionWhatsappPhoneError = false;
      }
    },

    initStudentLeaveReqPayload(student) {
      this.newReqDocList = [];
      this.reqObj = {
        id: 0,
        sID: student.id,
        request_date: new Date().toJSON(),
        reason: "",
        leaving_date: null,
        note: "",
        document: "",
        status: "pending",
        requested_by: this.$store.state.userData.userID,
        campusID: student.campusID || this.$store.state.userData.cId,
        type: "general",
        approved_by: 0,
        approved_document: "",
      };
      this.clearNewReqFileInput();
    },

    resetNewReqValidationRefs() {
      ["req_date", "req_reason", "req_st", "req_new_admission_details"].forEach(
        (r) => {
          const el = this.$refs[r];
          if (el && typeof el.state !== "undefined") el.state = undefined;
        }
      );
    },

    statusVariant(status) {
      const s = (status || "").toLowerCase();
      const vt =
        s == "present"
          ? "light-success"
          : s == "registered"
          ? "light-warning"
          : "light-danger";
      return vt;
    },

    normalizeStudentSearchResponse(response) {
      let list = [];
      if (response && response.data && Array.isArray(response.data)) {
        list = response.data;
      } else if (Array.isArray(response)) {
        list = response;
      }
      return list;
    },

    async searchStudentsByFederalBoard() {
      const q = (this.federalBoardQuery || "").trim();
      if (!q) {
        this.$bvToast.toast("Enter Federal Board registration number.", {
          title: this.$t("Error"),
          variant: "warning",
          solid: true,
        });
        return;
      }
      this.studentSearchLoading = true;
      this.federalBoardSearchAttempted = true;
      try {
        const url =
          this.$store.state.domain +
          "StudentDetails/SearchDetails?db=" +
          this.$store.state.userData.db +
          "&keyword=" +
          encodeURIComponent(q) +
          "&type=";
        const response = await this.get({
          url,
          token: this.$store.state.userData.token,
        });
        this.studentSearchResults =
          this.normalizeStudentSearchResponse(response);
      } catch (e) {
        console.error(e);
        this.studentSearchResults = [];
        this.$bvToast.toast("Unable to search students.", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.studentSearchLoading = false;
      }
    },

    async selectStudentForNewRequest(student) {
      this.newAdmissionMode = false;
      this.selectedStudentForRequest = { ...student };
      this.initStudentLeaveReqPayload(student);
      const opts = this.leaveReqTypeOptions;
      this.reqObj.type = opts.some((o) => o.value === "transfer")
        ? "transfer"
        : "general";
      let text = "";
      if (["myskoolhpgs", "myskoolprofectus"].includes(this.reportDomain)) {
        text = `Gr: ${student.grNoLocal || student.grNo} CID: ${student.grNo}`;
      } else {
        text = `Gr: ${student.grNo} ID: ${student.id}`;
      }
      this.sidebarTitle = `${student.name}<br/>${text}`;
      this.newRequestStep = "form";
      await this.$nextTick();
      this.resetNewReqValidationRefs();
    },

    clearSelectedStudentForNewRequest() {
      this.selectedStudentForRequest = null;
      this.reqObj = {};
      this.newReqDocList = [];
      this.newRequestStep = "search";
      this.sidebarTitle = "Search student";
      this.clearNewReqFileInput();
    },

    backFromNewRequestForm() {
      this.clearSelectedStudentForNewRequest();
    },

    async addNewRequestDoc() {
      const input = this.$refs.newLeaveReqDocsInput;
      if (!input || !input.files || input.files.length === 0) return;
      this.newReqDocLoading = true;
      try {
        const uploaded = await uploadToMySkool(input.files);
        getUploadedPaths(uploaded).forEach((path) => {
          this.newReqDocList.push(path);
        });
        this.reqObj.document = this.newReqDocList.join(",");
      } catch (err) {
        console.log(err);
      } finally {
        this.newReqDocLoading = false;
        this.clearNewReqFileInput();
      }
    },

    removeNewRequestDoc(index) {
      this.newReqDocList.splice(index, 1);
      this.reqObj.document = this.newReqDocList.join(",");
    },

    CheckNewReqDate() {
      var elem = this.$refs["req_date"];
      if (!elem) return false;
      if (!this.reqObj.request_date) {
        return (elem.state = false);
      }
      return (elem.state = true);
    },

    CheckNewReqReason() {
      if (this.newAdmissionMode) {
        const elem = this.$refs.req_new_admission_details;
        const n = this.newAdmissionForm;
        const whatsappOk =
          String(n.whatsapp_no || "").trim() !== "" &&
          !this.newAdmissionWhatsappPhoneError;
        const filled =
          (n.student_name || "").trim() !== "" &&
          (n.father_name || "").trim() !== "" &&
          (n.federal_board_reg_no || "").trim() !== "" &&
          whatsappOk;
        if (!filled) {
          if (elem) elem.state = false;
          return false;
        }
        if (elem) elem.state = true;
        this.reqObj.reason = this.buildNewAdmissionReasonFromForm();
        return true;
      }
      var elem = this.$refs["req_reason"];
      if (!elem) return false;
      var reason = (this.reqObj.reason || "").trim();
      if (reason === "") {
        return (elem.state = false);
      }
      return (elem.state = true);
    },

    async saveNewLeaveRequest() {
      if (
        this.CheckNewReqReason() !== true ||
        this.CheckNewReqDate() !== true
      ) {
        return;
      }

      this.reqObj.leaving_date = this.reqObj.request_date;
      this.savingNewRequest = true;
      var status = await this.post({
        url:
          this.$store.state.domain +
          "studentLeftRequest?db=" +
          this.$store.state.userData.db,
        body: this.reqObj,
        message: "Request saved successfully!",
        context: this,
        token: this.$store.state.userData.token,
      });
      this.savingNewRequest = false;
      if (status) {
        this.visibility5 = false;
        this.LoadData(this.currentStatus);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
::v-deep .b-sidebar {
  border-top-left-radius: 20px;
  border-bottom-left-radius: 20px;
}
.custom-sidebar-header {
  background: linear-gradient(135deg, #f4edff 0%, #e0d4ff 100%);
  border-bottom: 1px solid #e0d8ff;
  border-top-left-radius: 20px;
}

.all-applications-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 2rem;
  position: relative;
  overflow: hidden;

  .gradient-line-wrapper {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 4px;
    background: linear-gradient(90deg, #7367f0 0%, #9e95f5 100%);
  }
}

.resource-title {
  color: #2d1b69;
  font-weight: 800;
  letter-spacing: -0.5px;
  font-size: 1.4rem;
}

.filter-row {
  padding: 0.5rem 0;
  border-radius: 15px;
}

.filter-control {
  &.search-group {
    ::v-deep .input-group-prepend {
      margin-right: -1px; // Ensure overlap to remove white shadow gap
      .input-group-text {
        background: #ffffff;
        border: 1.5px solid #e0d8ff;
        border-right: none;
        border-radius: 12px 0 0 12px;
        color: #7367f0;
        padding-left: 1.25rem;
        transition: all 0.3s ease;
        box-shadow: none; // Remove potential shadows
      }
    }
    ::v-deep .form-control {
      border-left: none;
      padding-left: 0.5rem;
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
    }
  }

  ::v-deep .form-control {
    height: 46px;
    border-radius: 12px;
    border: 1.5px solid #e0d8ff;
    padding-left: 1.25rem;
    font-weight: 500;
    color: #2d1b69;
    transition: all 0.3s ease;

    &::placeholder {
      color: #5e5873; // Blackish/Dark neutral color
      font-weight: 500;
      opacity: 0.8;
    }

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 4px 12px rgba(115, 103, 240, 0.12);
      background: #fff;
    }
  }

  &.campus-select {
    ::v-deep .vs__dropdown-toggle {
      height: 46px;
      border-radius: 12px;
      border: 1.5px solid #e0d8ff;
      background: #ffffff;
      padding-top: 2px;
      transition: all 0.3s ease;

      &:hover {
        border-color: #7367f0;
      }
    }
    ::v-deep .vs__selected {
      font-weight: 500;
      color: #2d1b69;
    }
    ::v-deep .vs__search::placeholder {
      color: #b4a9e0;
    }
  }

  ::v-deep .form-control {
    height: 46px;
    border-radius: 10px;
    border: 1.5px solid #e0d8ff;
    padding-left: 1rem;
    font-weight: 500;
    transition: all 0.3s ease;

    &:focus {
      border-color: #7367f0;
      box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.1);
    }
  }
}

.action-btn-filter {
  height: 46px;
  font-weight: 600;
  border-radius: 10px;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.2);
  }
}

.selection-dot-badge {
  position: absolute;
  top: -8px;
  right: -8px;
  min-width: 22px;
  height: 22px;
  border-radius: 10px;
  display: flex !important;
  align-items: center;
  justify-content: center;
  padding: 0 6px !important;
  font-size: 0.7rem !important;
  z-index: 10;
  border: 2px solid #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  background: #7367f0 !important;
  color: #fff !important;
}

.all-applications-table {
  margin-top: 1rem;
  border-radius: 12px;
  min-width: 100%;
  width: max-content;
  border: 1px solid #e5e7eb;
  border-collapse: separate;
  border-spacing: 0;

  ::v-deep th {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border: none;
    font-weight: 700;
    color: #1e293b;
    text-transform: uppercase;
    font-size: 0.7rem;
    letter-spacing: 0.5px;
    padding: 0.75rem 0.875rem;
    border-bottom: 1px solid #e2e8f0;
    white-space: nowrap;
    position: sticky;
    top: 0;
    z-index: 5;

    &::after {
      display: none;
    }
  }

  ::v-deep td {
    vertical-align: middle;
    border-top: 1px solid #f1f5f9;
    border-bottom: 1px solid #f8fafc;
    padding: 0.75rem 0.875rem;
    background: #ffffff;
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    font-size: 0.875rem;
    color: #475569;
    white-space: nowrap;
  }

  ::v-deep tbody tr {
    transition: all 0.2s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    position: relative;
    border-left: 2px solid transparent;

    &:nth-child(even) {
      background: #f8fafc;
      td {
        background: #f8fafc;
      }
    }

    &:hover {
      background: #f3f0ff !important;
      border-left-color: #7c5dfa;
      td {
        background: #f3f0ff !important;
        color: #2d1b69;
      }
    }
  }

  @media (min-width: 992px) {
    ::v-deep .sticky-sno,
    ::v-deep .sticky-sno-2,
    ::v-deep .sticky-name,
    ::v-deep .sticky-campus,
    ::v-deep .sticky-name-campus {
      position: -webkit-sticky !important;
      position: sticky !important;
      z-index: 2 !important;
      background: #fff;
    }

    ::v-deep th.sticky-sno,
    ::v-deep th.sticky-sno-2,
    ::v-deep th.sticky-name,
    ::v-deep th.sticky-campus,
    ::v-deep th.sticky-name-campus {
      z-index: 15 !important;
      background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%) !important;
    }

    ::v-deep .sticky-sno {
      left: 0 !important;
      min-width: 40px;
      max-width: 40px;
      text-align: center;
    }

    ::v-deep .sticky-sno-2 {
      left: 0 !important;
      min-width: 60px;
      max-width: 60px;
    }

    ::v-deep .sticky-campus {
      left: 60px !important;
      min-width: 250px;
      max-width: 250px;
    }

    ::v-deep .sticky-name {
      left: 60px !important;
      min-width: 250px;
      max-width: 250px;
    }

    ::v-deep .sticky-name-campus {
      left: 310px !important;
      min-width: 250px;
      max-width: 250px;
    }

    // Match table striping for sticky cells
    ::v-deep tbody tr:nth-child(even) {
      .sticky-sno,
      .sticky-sno-2,
      .sticky-name,
      .sticky-campus,
      .sticky-name-campus {
        background: #f8fafc !important;
      }
    }

    // Match table hover for sticky cells
    ::v-deep tbody tr:hover {
      .sticky-sno,
      .sticky-sno-2,
      .sticky-name,
      .sticky-campus,
      .sticky-name-campus {
        background: #f3f0ff !important;
      }
    }
  }
}

.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: #fff !important;

  &.delete-btn {
    border: 2px solid #ef4444 !important;
    color: #ef4444 !important;
    &:hover {
      background: #fef2f2 !important;
      transform: translateY(-2px);
    }
  }
}

.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(124, 93, 250, 0.3);
  }
}

// Pagination Controls Styles
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
  }
}

// Compact Page Size Select
.page-size-select-compact {
  min-width: 75px;
  width: 75px;

  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;
    display: flex !important;
    align-items: center !important;

    &:hover {
      border-color: #b8a9ff !important;
    }

    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }

  ::v-deep .vs__selected-options {
    display: flex !important;
    align-items: center !important;
    padding: 0 !important;
  }

  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1 !important;
    display: flex !important;
    align-items: center !important;
    height: 100%;
    visibility: visible !important;
    opacity: 1 !important;
  }

  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }

  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
  }
}

.sb-form-stack {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.sb-student-pick-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: min(320px, 55vh);
  overflow-y: auto;
  padding-right: 2px;
}

.sb-student-pick-item {
  width: 100%;
  text-align: left;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 0.65rem 0.75rem;
  background: #fff;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    border-color: #a78bfa;
    box-shadow: 0 2px 8px rgba(124, 93, 250, 0.12);
  }
}

.sb-selected-student {
  border: 1px solid #bbf7d0;
  border-radius: 12px;
  padding: 0.75rem;
  background: linear-gradient(135deg, rgba(240, 253, 244, 0.9) 0%, #fff 100%);
}

.sb-new-admission-grid {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.sb-na-field {
  width: 100%;
}

.sb-na-label {
  display: block;
  font-size: 0.75rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.35rem;
}

.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;

  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;

    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
}

.pagination-wrapper-compact {
  display: flex;
  align-items: center;

  ::v-deep .pagination {
    margin-bottom: 0;

    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;

      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }

    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }

    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

// Table Wrapper for Horizontal Scroll
.table-scroll-container {
  position: relative;
  width: 100%;
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;
  scrollbar-width: none; // Safari/Chrome handle below

  &::-webkit-scrollbar {
    display: none;
  }
}

// Top Scrollbar for PC Users
.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }

  @media (max-width: 768px) {
    display: none;
  }
}

// Skeleton Loader Styles
.skeleton-row {
  animation: skeletonPulse 2s ease-in-out infinite;
  border-bottom: 1px solid #f1f5f9;
}

.skeleton-cell {
  padding: 1.25rem 1rem !important;
  background: #fff;
  vertical-align: middle;

  &:nth-child(1) .skeleton-loader {
    width: 40px;
    margin: 0 auto;
  }
  &:nth-child(2) .skeleton-loader {
    width: 220px;
  }
  &:nth-child(3) .skeleton-loader {
    width: 120px;
  }
  &:nth-child(4) .skeleton-loader {
    width: 140px;
  }
  &:nth-child(5) .skeleton-loader {
    width: 100px;
  }
  &:nth-child(6) .skeleton-loader {
    width: 100px;
  }
  &:nth-child(7) .skeleton-loader {
    width: 80px;
  }
  &:nth-child(8) .skeleton-loader {
    width: 80px;
    margin: 0 auto;
  }
}

.skeleton-loader {
  height: 18px;
  border-radius: 10px;
  background: linear-gradient(
    90deg,
    #f0f0f0 0%,
    #f8f8f8 25%,
    #f0f0f0 50%,
    #f8f8f8 75%,
    #f0f0f0 100%
  );
  background-size: 300% 100%;
  animation: skeletonShimmer 2s ease-in-out infinite;
  width: 100%;
  position: relative;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 255, 255, 0.4) 50%,
      transparent 100%
    );
    animation: skeletonShine 2s ease-in-out infinite;
  }
}

@keyframes skeletonPulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.85;
    transform: scale(0.998);
  }
}

@keyframes skeletonShimmer {
  0% {
    background-position: -300% 0;
  }
  100% {
    background-position: 300% 0;
  }
}

@keyframes skeletonShine {
  0% {
    left: -100%;
  }
  100% {
    left: 100%;
  }
}

@media (max-width: 992px) {
  .pagination-controls-top {
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-content: center;

    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }
}

.pill-badge-user {
  padding: 0.6rem 1rem !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  display: inline-flex !important;
  align-items: center;
  transition: all 0.3s ease;
  border: 1px solid transparent;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.05);
  }

  &.badge-light-primary {
    background: rgba(115, 103, 240, 0.08) !important;
    color: #7367f0 !important;
    border-color: rgba(115, 103, 240, 0.15) !important;
  }

  &.badge-light-success {
    background: rgba(40, 199, 111, 0.08) !important;
    color: #28c76f !important;
    border-color: rgba(40, 199, 111, 0.15) !important;
  }

  &.badge-light-secondary {
    background: rgba(130, 134, 139, 0.08) !important;
    color: #82868b !important;
    border-color: rgba(130, 134, 139, 0.15) !important;
  }

  &.badge-light-danger {
    background: rgba(234, 84, 85, 0.08) !important;
    color: #ea5455 !important;
    border-color: rgba(234, 84, 85, 0.15) !important;
  }
}

@media (max-width: 768px) {
  .filter-row {
    > div {
      margin-bottom: 0.75rem;
    }
  }

  .all-applications-list-card {
    padding: 1rem;
    border-radius: 15px;
  }
}
// ---- Sidebar Header ----
.resource-sidebar {
  ::v-deep .b-sidebar {
    width: 420px !important;
    max-width: 100%;
  }
}

.sb-header {
  padding: 1.25rem 1.5rem 1.5rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
  }

  &--approve {
    background: linear-gradient(135deg, #f0fdf4 0%, #dcfce7 60%, #bbf7d0 100%);
    border-bottom: 1px solid #bbf7d0;
    &::before {
      background: linear-gradient(90deg, #22c55e 0%, #4ade80 100%);
    }
    .sb-avatar {
      box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.25);
    }
  }

  &--reject {
    background: linear-gradient(135deg, #fff1f2 0%, #ffe4e6 60%, #fecdd3 100%);
    border-bottom: 1px solid #fecdd3;
    &::before {
      background: linear-gradient(90deg, #ef4444 0%, #f87171 100%);
    }
    .sb-avatar {
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.25);
    }
  }

  &--view {
    background: linear-gradient(135deg, #faf9ff 0%, #ede9fe 60%, #ddd6fe 100%);
    border-bottom: 1px solid #ddd6fe;
    &::before {
      background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 100%);
    }
    .sb-avatar {
      box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.25);
    }
  }
}

.sb-header__top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.sb-header__badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.3rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  background: rgba(124, 93, 250, 0.12);
  color: #5b21b6;

  &--danger {
    background: rgba(239, 68, 68, 0.12);
    color: #b91c1c;
  }
  &--info {
    background: rgba(124, 93, 250, 0.12);
    color: #5b21b6;
  }
}

.sb-close-btn {
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(0, 0, 0, 0.08);
  color: #64748b;
  transition: all 0.25s ease;
  backdrop-filter: blur(4px);

  &:hover {
    background: #fff;
    color: #1e293b;
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.sb-header__profile {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.sb-avatar {
  flex-shrink: 0;
  font-size: 1.1rem !important;
  font-weight: 700 !important;
}

.sb-header__info {
  flex: 1;
  min-width: 0;
}

.sb-header__name {
  font-size: 1rem;
  font-weight: 800;
  color: #1e293b;
  line-height: 1.35;
  margin: 0;
  word-break: break-word;

  ::v-deep br {
    display: block;
    content: "";
    margin-top: 2px;
  }
}

.sb-body {
  padding: 1.5rem;
  background: #f8f9fc;
  min-height: calc(100vh - 160px);
}

.sb-divider {
  display: flex;
  align-items: center;
  margin: 1.25rem 0;
  gap: 0.75rem;
  color: #94a3b8;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    height: 1px;
    background: #e2e8f0;
  }
}

.sb-form-field {
  margin-bottom: 1.25rem;
}

.sb-label {
  display: flex;
  align-items: center;
  font-size: 0.8125rem;
  font-weight: 600;
  color: #475569;
  margin-bottom: 0.4rem;
}

.sb-input {
  border-radius: 10px !important;
  border: 1.5px solid #e2e8f0 !important;
  height: 42px !important;
  background: #fff !important;
  transition: all 0.2s ease;
  font-size: 0.875rem;

  &:focus {
    border-color: #7c5dfa !important;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1) !important;
  }
}

.sb-textarea {
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  transition: all 0.2s ease;
  font-size: 0.875rem;
  resize: vertical;
  width: 100%;
  padding: 0.625rem 0.875rem;

  &:hover {
    border-color: #c4b5fd;
  }
  &:focus {
    border-color: #7c5dfa;
    box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1);
    outline: none;
  }
}

.sb-vselect {
  ::v-deep .vs__dropdown-toggle {
    height: 42px !important;
    border-radius: 10px !important;
    border: 1.5px solid #e2e8f0 !important;
    background: #fff !important;
    transition: all 0.2s ease;

    &:hover {
      border-color: #c4b5fd !important;
    }
  }

  ::v-deep .vs__selected {
    font-weight: 500 !important;
    color: #2d1b69 !important;
  }

  ::v-deep .vs__dropdown-menu {
    border-radius: 12px !important;
    border: 1px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    padding: 0.5rem !important;
  }
}

.sb-action-btn {
  border-radius: 12px;
  padding: 0.75rem;
  font-weight: 700;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
}

.doc-thumb-img {
  width: 100%;
  height: 72px;
  object-fit: cover;
  border-radius: 6px;
}
</style>
<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";
</style>
